import {Component, ElementRef, HostListener, inject, input, output} from '@angular/core';
import {Product} from '../../../../interfaces/responses/product-response.interfaces';
import {DatePipe} from '@angular/common';
import {Router} from '@angular/router';
import {ImagesService} from '../../../../services/general/images.service';
import {ProductService} from '../../../../services/products/product.service';
import {ToastService} from '../../../../services/general/toast.service';
import {ConfirmationDialogComponent} from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import {PermissionService} from '../../../../services/permission/permission.service';

@Component({
  selector: 'app-new-application-table',
  imports: [
    DatePipe,
    ConfirmationDialogComponent
  ],
  templateUrl: './new-application-table.component.html',
  styleUrl: './new-application-table.component.scss'
})
export class NewApplicationTableComponent {
  _router = inject(Router);
  _images = inject(ImagesService);
  permissionService = inject(PermissionService);
  productService = inject(ProductService);
  _toast = inject(ToastService);
  _eref = inject(ElementRef);
  isUpdateProduct = input<boolean>(true);
  showDeleteDialog: boolean = false;
  showStatusDialog: boolean = false;
  isActionClick: boolean = false;

  product = input<Product>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.isActionClick = false;
    }
  }

  showDeleteConfirmation() {
    this.showDeleteDialog = true;
  }

  cancelDelete() {
    this.showDeleteDialog = false;
  }

  confirmDelete() {
    const id = this.product()?.id;
    if (!id) {
      console.error('Product ID is undefined. Cannot delete product.');
      return;
    }

    this.productService.deleteProduct(id).subscribe({
      next: (response) => {
        this._toast.success('Product deleted successfully');
        this.showDeleteDialog = false;
        this.loadProductData();
      },
      error: (error) => {
        console.error('Error deleting product:', error);
        this._toast.error('Error deleting product');
        this.showDeleteDialog = false;
      }
    });
  }

  loadProductEvent = output<void>()
  loadProductData() {
    this.loadProductEvent.emit();
  }

  updateProductEvent = output<Product>()
  showOverlayEvent = output<void>()

  updateProduct() {
    const product = this.product();
    if (!product) {
      console.error('Product is undefined. Cannot update product.');
      return;
    }
    this.updateProductEvent.emit(product);
    this.showOverlayEvent.emit();
  }

  onViewProduct() {
    this._router.navigate(
      ['product-details', this.product()?.id],
      { state: {id: this.product()?.id, name: this.product()} },
    )
  }

  onEditClick() {
    if (!this.permissionService.canManage('products')) return;
    this.updateProduct();
  }

  onActionClick() {
    this.isActionClick = !this.isActionClick;
  }

  get dropdownOptions(): any[] {
    const product = this.product();
    const isActive = product?.isActive ?? false;
    const canManage = this.permissionService.canManage('products');

    const options = [
      {
        title: 'View',
        icon: this._images.arrowForward
      },
      {
        title: 'Edit',
        icon: this._images.editIcon
      },
      {
        title: isActive ? 'Deactivate' : 'Activate',
        icon: isActive ? this._images.deniedIcon : this._images.checkIcon
      },
      {
        title: 'Delete',
        icon: this._images.deleteIcon
      },
    ];

    return canManage ? options : options.filter(option => option.title === 'View');
  }

  onOptionClick(id: string) {
    switch(id) {
      case 'View':
        this.onViewProduct();
        this.isActionClick = false;
        break;
      case 'Edit':
        this.onEditClick();
        this.isActionClick = false;
        break;
      case 'Activate':
      case 'Deactivate':
        this.showStatusConfirmation();
        this.isActionClick = false;
        break;
      case 'Delete':
        this.showDeleteConfirmation();
        this.isActionClick = false;
        break;
      default:
        return;
    }
  }

  showStatusConfirmation() {
    this.showStatusDialog = true;
  }

  cancelStatusToggle() {
    this.showStatusDialog = false;
  }

  confirmStatusToggle() {
    const id = this.product()?.id;
    if (!id) {
      console.error('Product ID is undefined. Cannot toggle product status.');
      this.showStatusDialog = false;
      return;
    }

    const product = this.product();
    const currentStatus = product?.isActive ?? false;
    const action = currentStatus ? 'deactivated' : 'activated';

    this.productService.toggleStatus(id).subscribe({
      next: (response) => {
        this._toast.success(`Product ${action} successfully`);
        this.showStatusDialog = false;
        this.loadProductData();
      },
      error: (error) => {
        console.error('Error toggling product status:', error);
        this._toast.error(`Error ${action} product`);
        this.showStatusDialog = false;
      }
    });
  }
}
